import React from 'react';
import Dialog from 'components/common/react-dialog';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance } from '@stripe/stripe-js';
import { PaymentError, PaymentInfo, PaymentLoading, PaymentSuccess } from 'components/common/Icon';
import { Button } from 'components/common/Button';
import { useStripePaymentStatus, useSetupStripe } from './hooks/useStripeCheckout';
import type { PaymentStatus } from './hooks/useStripeCheckout';
import Wahanda from 'common/wahanda';
import style from './StripeCheckout.scss';

interface CompletePageProps {
  onClose: () => void;
  supplierId: number;
}

interface CompletePageContentProps {
  onClose: () => void;
  supplierId: number;
}

const STATUS_CONTENT_MAP: Record<
  PaymentStatus,
  { text: string; iconColor: string; icon: JSX.Element }
> = {
  loading: {
    text: 'Retrieving payment status...',
    iconColor: '#6D6E78',
    icon: <PaymentLoading size="medium" />,
  },
  succeeded: {
    text: 'Payment succeeded',
    iconColor: '#30B130',
    icon: <PaymentSuccess size="medium" />,
  },
  processing: {
    text: 'Your payment is processing.',
    iconColor: '#6D6E78',
    icon: <PaymentInfo size="medium" />,
  },
  requires_payment_method: {
    text: 'Your payment was not successful, please try again.',
    iconColor: '#DF1B41',
    icon: <PaymentError size="medium" />,
  },
  default: {
    text: 'Something went wrong, please try again.',
    iconColor: '#DF1B41',
    icon: <PaymentError size="medium" />,
  },
};

const getStatusIconClass = (status: PaymentStatus): string => {
  switch (status) {
    case 'loading':
      return 'statusIconLoading';
    case 'succeeded':
      return 'statusIconSucceeded';
    case 'processing':
      return 'statusIconProcessing';
    case 'requires_payment_method':
      return 'statusIconRequiresPaymentMethod';
    case 'default':
      return 'statusIconDefault';
    default:
      return 'statusIconDefault';
  }
};

const appearance: Appearance = {
  theme: 'stripe',
};

const loader = 'auto';
const lang = Wahanda.lang.settings.supplier.payments.payNowDialog;

const CompletePageContent = ({ onClose, supplierId }: CompletePageContentProps) => {
  const { status } = useStripePaymentStatus({ supplierId });

  const statusContent = STATUS_CONTENT_MAP[status] || STATUS_CONTENT_MAP['default'];

  const successLang = lang.success;
  const sharedLang = Wahanda.lang.shared;

  const showSuccessConfirmation = () => (
    <div data-test="payment-success" className={style.container}>
      <div className={style.successMessage}>
        <div className={style.successIcon} />
        <div className={style.successTextBlock}>
          <div className={style.successHeader}>{successLang.header}</div>
          <div className={style.successBody}>{successLang.body}</div>
        </div>
      </div>
      <div className={style.actions}>
        <Button onClick={onClose} label={sharedLang.ok} fullWidth size="large" />
      </div>
    </div>
  );

  return (
    <>
      {status === 'succeeded' ? (
        showSuccessConfirmation()
      ) : (
        <div id="payment-status" className={style.paymentStatus}>
          <div
            id="status-icon"
            className={`${style.statusIcon} ${style[getStatusIconClass(status)]}`}
          >
            {statusContent.icon}
          </div>
          <h2 id="status-text" className={style.statusText}>
            {statusContent.text}
          </h2>
        </div>
      )}
    </>
  );
};

export const CompletePage = ({ supplierId, onClose }: CompletePageProps) => {
  const { stripePromise, clientSecret, error } = useSetupStripe({ supplierId });

  React.useEffect(() => {
    if (error) {
      Wahanda.ToastNotifications.sendMessage(error, { variant: 'danger' });
    }
  }, [error]);

  return (
    <Elements options={{ clientSecret, appearance, loader }} stripe={stripePromise}>
      <Dialog title="Payment Status" width={620} onClose={onClose}>
        <CompletePageContent onClose={onClose} supplierId={supplierId} />
      </Dialog>
    </Elements>
  );
};
